import { countryLookup, validateTerritory } from './Address';

import flatten from 'flat';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import moment from 'moment';
import { translate } from './i18n';

/** Characters that can trigger formula injection at start of cell (Excel/Sheets). @type {string[]} */
export const CSV_FORMULA_CHARS = ['=', '@', '+', '-', '|', '\t', ';'];

/**
 * Base CSV escaping; optional formulaProtection prefixes formula chars and quotes newlines per RFC 4180.
 * @param {any} value - Value to escape
 * @param {Object} [options] - formulaProtection: when true, prevents formula interpretation
 * @returns {string} Escaped CSV-safe value
 */
const escapeCSV = (value, { formulaProtection = false } = {}) => {
    // Return empty string for null/undefined values, but not for 0 or false
    if (value === null || value === undefined) {
        return '';
    }
    const stringValue = String(value);

    // Check if value starts with a formula-triggering character
    const startsWithFormulaChar = CSV_FORMULA_CHARS.some(char => stringValue.startsWith(char));

    // Check if value contains characters that require quoting
    const containsQuotableCharacters =
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        // Newlines and carriage returns are quotable only when formula protection is enabled
        (formulaProtection && (stringValue.includes('\n') || stringValue.includes('\r')));

    if (startsWithFormulaChar) {
        const escaped = stringValue.replace(/"/g, '""');
        // With formula protection, prefix with single quote to prevent formula interpretation
        // Without it, just wrap in double quotes like other special characters
        return formulaProtection ? `"'${escaped}"` : `"${escaped}"`;
    }

    if (containsQuotableCharacters) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
};

/** Escapes a value for CSV export, neutralizing formula injection (CWE-74). @param {any} value @returns {string} */
export const escapeCSVValue = value => escapeCSV(value, { formulaProtection: true });

/**
 * Escapes value for DSAR CSV template; prevents formula injection and quotes special chars per RFC 4180.
 * @param {string|number|boolean|null|undefined} value
 * @returns {string} Escaped CSV-safe value
 */
export const escapeCSVTemplateValue = value => escapeCSV(value, { formulaProtection: true });

/** Sanitizes header for safe display (truncate 50 chars, strip dangerous chars). @param {string} header @returns {string} */
const sanitizeHeader = header =>
    String(header)
        .slice(0, 50)
        .replace(/[<>&'"]/g, '');

/** True if value starts with a formula-triggering character. @param {string} value @returns {boolean} */
export const containsFormulaInjection = value => {
    const stringValue = String(value ?? '');
    return CSV_FORMULA_CHARS.some(char => stringValue.startsWith(char));
};

/**
 * Validates headers for formula injection; returns isSafe and sanitized unsafeHeaders.
 * @param {string[]} headers
 * @returns {{isSafe: boolean, unsafeHeaders: string[]}}
 */
export const validateHeaderSafety = headers => {
    const unsafeHeaders = headers.filter(containsFormulaInjection);
    return {
        isSafe: unsafeHeaders.length === 0,
        unsafeHeaders: unsafeHeaders.map(sanitizeHeader),
    };
};

/**
 * Parses first line of CSV for headers; handles quotes and RFC 4180 ""; strips BOM.
 * @param {string} csvContent - Raw CSV content
 * @returns {string[]} Parsed header strings
 */
export const parseCSVHeaders = csvContent => {
    // Strip UTF-8 BOM if present (common in Excel-exported CSVs)
    const cleanContent = csvContent.replace(/^\uFEFF/, '');

    // Extract first line efficiently without creating full array
    const firstNewline = cleanContent.search(/\r?\n/);
    const firstLine = firstNewline === -1 ? cleanContent : cleanContent.slice(0, firstNewline);

    const headers = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < firstLine.length) {
        const char = firstLine[i];

        if (char === '"') {
            if (inQuotes && firstLine[i + 1] === '"') {
                // RFC 4180: "" inside quotes represents a literal quote
                current += '"';
                i += 2;
                continue;
            }
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            headers.push(current.trim());
            current = '';
        } else {
            current += char;
        }
        i++;
    }

    if (current) {
        headers.push(current.trim());
    }

    return headers.filter(h => h.length > 0);
};

/**
 * Validates parsed headers against allowed set (case-insensitive); rejects only disallowed columns.
 * @param {string[]} parsedHeaders - Headers from uploaded CSV
 * @param {string[]} allowedHeaders - Allowed by getExpectedCSVHeaders
 * @returns {{isValid: boolean, disallowedHeaders: string[]}}
 */
export const validateCSVHeaders = (parsedHeaders, allowedHeaders) => {
    const normalize = h => h.toLowerCase().trim();
    const normalizedAllowed = new Set(allowedHeaders.map(normalize));

    const disallowedHeaders = parsedHeaders.filter(h => !normalizedAllowed.has(normalize(h)));

    return {
        isValid: disallowedHeaders.length === 0,
        disallowedHeaders,
    };
};

/** Builds error message for disallowed columns; sanitizes headers for display. @param {string[]} disallowedHeaders @returns {string} */
export const buildHeaderErrorDetails = disallowedHeaders => {
    if (disallowedHeaders.length === 0) {
        return '';
    }
    const sanitized = disallowedHeaders.map(sanitizeHeader);
    return `Disallowed columns: ${sanitized.join(', ')}.`;
};

/**
 * Validates that all required headers are present (case-insensitive). Extra columns allowed.
 * @param {string[]} parsedHeaders - Headers from uploaded CSV
 * @param {string[]} requiredHeaders - Essential headers (e.g. from getRequiredImportHeaders)
 * @returns {{isValid: boolean, missingHeaders: string[]}}
 */
export const validateEssentialCSVHeaders = (parsedHeaders, requiredHeaders) => {
    const normalize = h => h.toLowerCase().trim();
    const parsedSet = new Set(parsedHeaders.map(normalize));
    const missingHeaders = requiredHeaders.filter(req => !parsedSet.has(normalize(req)));
    return {
        isValid: missingHeaders.length === 0,
        missingHeaders,
    };
};

/** Builds error details for missing required columns. @param {string[]} missingHeaders @returns {string} */
export const buildMissingHeaderErrorDetails = missingHeaders => {
    if (missingHeaders.length === 0) {
        return '';
    }
    const sanitized = missingHeaders.map(sanitizeHeader);
    return `Missing required columns: ${sanitized.join(', ')}.`;
};

/** Parses CSV content into rows/cells for country/territory column validation. */
const parseCountryTerritoryValidationRows = csvContent => {
    const rows = [];
    let row = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < csvContent.length) {
        const char = csvContent[i];
        const nextChar = csvContent[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                i += 2;
                continue;
            }
            inQuotes = !inQuotes;
            i++;
            continue;
        }

        if (!inQuotes && char === ',') {
            row.push(current.trim());
            current = '';
            i++;
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            row.push(current.trim());
            current = '';
            if (char === '\r' && nextChar === '\n') {
                i++;
            }

            const hasAnyValue = row.some(cell => cell !== '');
            if (hasAnyValue) {
                rows.push(row);
            }
            row = [];
            i++;
            continue;
        }

        current += char;
        i++;
    }

    if (current.length > 0 || row.length > 0) {
        row.push(current.trim());
        const hasAnyValue = row.some(cell => cell !== '');
        if (hasAnyValue) {
            rows.push(row);
        }
    }

    return rows;
};

const ISO_ALPHA2_REGEX = /^[A-Z]{2}$/;
const ISO_ALPHA2_CODES = new Set(Object.values(countryLookup).map(({ twoLetter }) => twoLetter));

/**
 * Validates optional country/territory CSV columns as ISO 3166-1 alpha-2 values when present.
 * @param {string} csvContent
 * @returns {{isValid: boolean, invalidEntries: {rowNumber: number, column: 'country'|'territory', value: string}[]}}
 */
export const validateCountryTerritoryColumns = csvContent => {
    const rows = parseCountryTerritoryValidationRows(csvContent || '');
    if (rows.length === 0) {
        return { isValid: true, invalidEntries: [] };
    }

    const headers = rows[0].map(header => header.toLowerCase().trim());
    const countryIndex = headers.indexOf('country');
    const territoryIndex = headers.indexOf('territory');

    if (countryIndex === -1 && territoryIndex === -1) {
        return { isValid: true, invalidEntries: [] };
    }

    const invalidEntries = [];
    const validateValue = (value, rowNumber, column, normalizedCountry = '') => {
        const trimmed = (value || '').trim();
        if (trimmed === '') {
            return;
        }

        const normalized = trimmed.toUpperCase();

        const hasCountryContext = column === 'territory' && normalizedCountry !== '';
        const isIsoAlpha2CountryCode =
            !hasCountryContext &&
            ISO_ALPHA2_REGEX.test(normalized) &&
            ISO_ALPHA2_CODES.has(normalized);
        const isCountryTerritoryCode =
            hasCountryContext && validateTerritory(normalized, normalizedCountry);

        if (!isIsoAlpha2CountryCode && !isCountryTerritoryCode) {
            invalidEntries.push({ rowNumber, column, value: normalized });
        }
    };

    rows.slice(1).forEach((row, index) => {
        const rowNumber = index + 2;
        const normalizedCountry =
            countryIndex !== -1 ? (row[countryIndex] || '').trim().toUpperCase() : '';

        if (countryIndex !== -1) {
            validateValue(row[countryIndex], rowNumber, 'country', normalizedCountry);
        }

        if (territoryIndex !== -1) {
            validateValue(row[territoryIndex], rowNumber, 'territory', normalizedCountry);
        }
    });

    return {
        isValid: invalidEntries.length === 0,
        invalidEntries,
    };
};

/** Builds display-safe details string for invalid country/territory values. */
export const buildCountryTerritoryErrorDetails = invalidEntries => {
    if (!invalidEntries || invalidEntries.length === 0) {
        return '';
    }

    const details = invalidEntries
        .map(({ rowNumber, column, value }) => {
            const safeColumn = sanitizeHeader(column);
            const safeValue = sanitizeHeader(value);
            return `row ${rowNumber} (${safeColumn}: ${safeValue})`;
        })
        .join(', ');

    return `Invalid ISO alpha-2 values at ${details}.`;
};

/** True if file appears to be CSV (MIME and extension). @param {File} file @returns {boolean} */
export const isValidCSVFile = file => {
    if (!file) {
        return false;
    }
    const validMimeTypes = ['text/csv', 'application/csv', 'text/plain'];
    const hasValidMimeType = validMimeTypes.includes(file.type) || file.type === '';
    const hasValidExtension = file.name.toLowerCase().endsWith('.csv');

    return hasValidMimeType && hasValidExtension;
};

// --- Object-to-CSV export helpers (request details download) ---

/** @param {*} value @returns {boolean} True if value is a primitive, Date, or moment. */
const isPrimitiveOrDate = value =>
    isNil(value) ||
    isString(value) ||
    isNumber(value) ||
    isBoolean(value) ||
    moment.isDate(value) ||
    moment.isMoment(value);

/** @param {*} value @returns {boolean} True if value is an array of primitives/dates. */
const isRepresentableArray = value => isArray(value) && value.every(isPrimitiveOrDate);

/** @param {*} value @returns {boolean} True if value can be represented in CSV. */
const csvIsValid = value => isPrimitiveOrDate(value) || isRepresentableArray(value);

/** @param {string|number|null|undefined} value @returns {boolean} */
const isTimestamp = value => isNumber(value) && !isNaN(value) && value.toString().length === 13;

/**
 * Formats a JS value to its string representation for CSV.
 * @param {*} value @returns {string}
 */
const csvFormat = value => {
    if (isNil(value) || isNaN(value)) {
        return '';
    }
    if (moment.isDate(value) || moment.isMoment(value)) {
        return moment(value).toISOString();
    }
    if (isBoolean(value)) {
        return value ? 'true' : 'false';
    }
    if (isNumber(value)) {
        if (isTimestamp(value)) {
            return moment(value).toISOString();
        }
        return String(value);
    }
    if (isString(value)) {
        return value;
    }
    if (isRepresentableArray(value)) {
        return value.map(csvFormat).join(', ');
    }
    return '';
};

/**
 * Converts a wildcard pattern to a RegExp.
 * '*' matches a single path segment, '**' matches any nested path.
 * @param {string} pattern @returns {RegExp}
 */
const patternToRegex = pattern => {
    const DOUBLE_STAR = '__DOUBLE_STAR__';
    const escaped = pattern
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\*\*/g, DOUBLE_STAR)
        .replace(/\*/g, '[^.]*')
        .replace(new RegExp(DOUBLE_STAR, 'g'), '.*');
    return new RegExp(`^${escaped}$`); // eslint-disable-line security/detect-non-literal-regexp
};

/** Converts any JS value to a CSV-safe string. @param {*} value @returns {string} */
const toCSV = value => escapeCSVValue(csvFormat(value));

/** @param {string} key @param {string[]} omitKeys @returns {boolean} */
const shouldOmit = (key, omitKeys) =>
    omitKeys.some(pattern =>
        pattern.includes('*') ? patternToRegex(pattern).test(key) : pattern === key
    );

/**
 * Converts an object to CSV-ready entries.
 * @param {object} object @param {string[]} omitKeys @returns {{ key: string, value: string }[]}
 */
const toCSVEntries = (object, omitKeys = []) => {
    const flattened = flatten(object, { delimiter: '.' });
    return Object.entries(flattened)
        .filter(([key, value]) => !shouldOmit(key, omitKeys) && csvIsValid(value))
        .map(([key, value]) => ({ key, value: toCSV(value) }));
};

/**
 * Makes a CSV string from the given object.
 * @param {object} object @param {string[]} omitKeys @returns {string}
 */
export const makeCSVFile = (object, omitKeys = []) => {
    const entries = toCSVEntries(object, omitKeys);
    const headers = entries.map(({ key }) => key).join(',');
    const values = entries.map(({ value }) => translate(value)).join(',');
    return [headers, values].join('\n');
};

/**
 * Exports an object as a CSV file download.
 * @param {{ object: object, omitKeys?: string[], filename: string }} data
 * @throws {Error} If the object is null or undefined.
 */
export const exportObjectAsCSV = ({ object, omitKeys = [], filename }) => {
    if (isNil(object)) {
        throw new Error('Object is required');
    }
    const csvFile = makeCSVFile(object, omitKeys);
    const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
};
